import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import keyBy from 'lodash/keyBy';
import { defineStore, acceptHMRUpdate } from 'pinia';
import type { DxFilter } from '@pendo/middleware';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import {
    WORKFLOWS_SUB_USER_SETTING_NAME, WORKFLOW_USER_SETTINGS, DEFAULT_WORKFLOW_MAX_DURATION, DEFAULT_WORKFLOW_MAX_INTERVAL } from '@/workflows/constants/workflow.constants';
import { getDefaultTimeseries } from '@/reports/utils';
import { HISTORY_TYPES } from '@/stores/constants/user-settings-history.constants';
import { fetchAllWorkflowsForSubscription, fetchWorkflowForSubscription, createWorkflow, deleteWorkflow, updateWorkflow } from '@/stores/api/saved-reports.api';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useHistoryStore } from '@/stores/history.store';
import type { HistoryItem } from '@/stores/history.store';
import { sendReportObjectMetadata } from '@/utils/pendo-object-metadata';

let fetchPromise: Promise<unknown[]> | null = null;

interface WorkflowRecord {
    id: string;
    name?: string;
    description?: string;
    createdByUser?: {
        username?: string;
    };
    timeSeries?: unknown;
    segmentId?: string;
    metadataFilters?: DxFilter[];
    journey?: {
        pageIds: unknown[];
        featureIds: unknown[];
    };
    [key: string]: unknown;
}

interface WorkflowHistoryEntry {
    id: string;
    timestamp: number;
}

interface UserSetting<T> {
    name?: string;
    value: T;
}

interface IncludedStep {
    id: string;
    stepId?: string;
    name?: string;
    label?: string;
    type?: string;
    featureId?: string;
}

interface WorkflowsState {
    isFetching: boolean;
    isRemoving: boolean;
    isSaving: boolean;
    map: Record<string, WorkflowRecord>;
    includedSteps: Record<string, unknown>;
    savedWorkflowId: string | null;
    workingWorkflow: Record<string, unknown>;
    insightsShowing: boolean;
}

// Exported only for unit tests — do not call in production code.
export function __resetFetchPromiseForUnitTests () { fetchPromise = null; }

function getInitialWorkflowState (nowDate: Date) {
    return {
        classification: 'nonrecurring',
        id: null as unknown,
        maxDuration: DEFAULT_WORKFLOW_MAX_DURATION,
        maxInterval: DEFAULT_WORKFLOW_MAX_INTERVAL,
        name: `New Workflow - ${moment(nowDate).format(DATE_FORMAT.short)} @ ${moment(nowDate).format(DATE_FORMAT.time)}`,
        segmentId: 'everyone',
        metadataFilters: [] as DxFilter[],
        subscriptionSharing: {
            permission: 'restricted',
        },
        timeSeries: getDefaultTimeseries('Workflow'),
        workflowSteps: [] as unknown[],
        journey: {
            featureIds: [] as unknown[],
            pageIds: [] as unknown[],
        },
    };
}

const defaultWorkflowSettings = Object.freeze({
    [WORKFLOW_USER_SETTINGS.DATA_SOURCE]: '',
    [WORKFLOW_USER_SETTINGS.TIME_PERIOD]: '',
    [WORKFLOW_USER_SETTINGS.TOTAL_CHART_DATA_SOURCE]: '',
    [WORKFLOW_USER_SETTINGS.GUIDE_MONITOR_OVER_TIME_CHART]: '',
    [WORKFLOW_USER_SETTINGS.TIME_TO_COMPLETE_CHART_DATA_TYPE]: '',
    [WORKFLOW_USER_SETTINGS.TIME_TO_COMPLETE_CHART_TIME_PERIOD]: '',
    [WORKFLOW_USER_SETTINGS.VIEW_BY]: {},
    [WORKFLOW_USER_SETTINGS.BASELINE_DATE_RANGE]: null,
});

function augmentWorkflowWithDefaults (workflow: WorkflowRecord) {
    if (!workflow.timeSeries) workflow.timeSeries = getDefaultTimeseries('Workflow');
    if (!workflow.segmentId) workflow.segmentId = 'everyone';
}

function getInitialState (): WorkflowsState {
    fetchPromise = null;

    return {
        isFetching: false,
        isRemoving: false,
        isSaving: false,
        map: {},
        includedSteps: {},
        savedWorkflowId: null,
        workingWorkflow: {},
        insightsShowing: false,
    };
}

export const useWorkflowsStore = defineStore('workflows', {
    state: getInitialState,
    getters: {
        list (state) {
            return Object.values(state.map);
        },
        workflowById (state) {
            return (id: string) => get(state.map, id, null);
        },
        savedWorkflow (): unknown {
            return this.workflowById(this.savedWorkflowId as string);
        },
        workflowHistory (state): HistoryItem[] {
            return (useHistoryStore().itemHistory(HISTORY_TYPES.WORKFLOW) as WorkflowHistoryEntry[])
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name as string,
                    createdBy: state.map[id].createdByUser!.username as string,
                    description: state.map[id].description as string,
                    historyType: HISTORY_TYPES.WORKFLOW,
                    routeName: 'WorkflowDetails',
                    label: 'Workflow',
                    timestamp,
                    starred: false,
                }));
        },
        savedWorkflowSettingsById () {
            return (id: string) => {
                const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);

                if (id === 'new' || !workflowsSubSettings) return defaultWorkflowSettings;

                const { value: workflowsSettingsMap } = workflowsSubSettings as UserSetting<Record<string, unknown>>;

                return workflowsSettingsMap[id];
            };
        },
        savedWorkflowSettings (state): unknown {
            const workflowId = state.savedWorkflowId;
            const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);

            if (workflowId === 'new' || !workflowsSubSettings) return defaultWorkflowSettings;

            const { value: workflowsSettingsMap } = workflowsSubSettings as UserSetting<Record<string, unknown>>;

            return workflowsSettingsMap[workflowId as string];
        },
        isLoading (state) {
            return state.isSaving || state.isFetching;
        },
    },
    actions: {
        deleteMapKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setFetching ({ isFetching }: { isFetching: boolean }) {
            this.isFetching = isFetching;
        },
        setIncludedSteps ({ updatedSteps }: { updatedSteps: Record<string, unknown> }) {
            this.includedSteps = cloneDeep(updatedSteps);
        },
        setMap ({ map }: { map: Record<string, WorkflowRecord> }) {
            this.map = map;
        },
        setMapAtKey ({ id, workflow }: { id: string; workflow: WorkflowRecord }) {
            this.map[id] = workflow;
        },
        setRemoving ({ isRemoving }: { isRemoving: boolean }) {
            this.isRemoving = isRemoving;
        },
        setSaving ({ isSaving }: { isSaving: boolean }) {
            this.isSaving = isSaving;
        },
        setSavedWorkflowId ({ id }: { id: string | null }) {
            this.savedWorkflowId = id;
        },
        setWorkingWorkflow ({ workflow = getInitialWorkflowState(new Date()) }: { workflow?: Record<string, unknown> } = {}) {
            this.workingWorkflow = workflow;
        },
        setWorkingWorkflowField ({ field, value }: { field: string; value: unknown }) {
            // Reassign the clone so nested field changes stay reactive.
            const workingWorkflow = cloneDeep(this.workingWorkflow);
            set(workingWorkflow, field, value);
            this.workingWorkflow = workingWorkflow;
        },
        showInsights () {
            this.insightsShowing = true;
        },
        hideInsights () {
            this.insightsShowing = false;
        },
        toggleInsights () {
            this.insightsShowing = !this.insightsShowing;
        },
        async loadAll ({ useCache = true, excludeJourneyDefinition = true }: { useCache?: boolean; excludeJourneyDefinition?: boolean } = {}) {
            if (this.isFetching) {
                await fetchPromise;

                return;
            }

            if (!isEmpty(this.map) && useCache) {
                return;
            }

            this.setFetching({ isFetching: true });

            fetchPromise = fetchAllWorkflowsForSubscription(excludeJourneyDefinition) as Promise<unknown[]>;

            const list = await fetchPromise;
            (list as WorkflowRecord[]).forEach((workflow) => {
                if (workflow.journey) workflow.journey = { pageIds: [], featureIds: [] };
                augmentWorkflowWithDefaults(workflow);
            });

            this.setMap({ map: keyBy(list as WorkflowRecord[], 'id') });
            this.setFetching({ isFetching: false });
        },
        async loadOne ({ id }: { id: string }) {
            let workflow;
            try {
                workflow = await fetchWorkflowForSubscription(id);
            } catch (error) {
                throw error;
            }
            augmentWorkflowWithDefaults(workflow);
            this.setMapAtKey({ id, workflow });
        },
        async loadByIds (ids: string[]) {
            try {
                const workflows = await Promise.all(
                    ids.map(async (id) => {
                        try {
                            const workflow = await fetchWorkflowForSubscription(id);

                            return workflow;
                        } catch {
                            return null;
                        }
                    }),
                );
                (workflows as Array<WorkflowRecord | null>)
                    .filter((workflow): workflow is WorkflowRecord => workflow !== null)
                    .forEach((workflow) => {
                        augmentWorkflowWithDefaults(workflow);
                        this.setMapAtKey({ id: workflow.id, workflow });
                    });
            } catch (error) {
                throw error;
            }
        },
        async create ({ workflow }: { workflow?: unknown } = {}) {
            this.setSaving({ isSaving: true });
            try {
                const newWorkflow = await createWorkflow(workflow) as WorkflowRecord;

                const queryParams = new URLSearchParams(window.location.search);
                const globalCreate = queryParams.has('globalCreate');
                window.pendo?.track('report-created', {
                    type: 'Workflow',
                    ...(globalCreate && { location: 'globalCreate' }),
                });

                this.setMapAtKey({ id: newWorkflow.id, workflow: newWorkflow });
                this.setSaving({ isSaving: false });
                sendReportObjectMetadata(newWorkflow);

                return newWorkflow.id;
            } catch (error) {
                this.setSaving({ isSaving: false });
                throw error;
            }
        },
        async update ({ workflow, applyToAggregation }: { workflow: WorkflowRecord; applyToAggregation?: unknown }) {
            this.setSaving({ isSaving: true });
            try {
                augmentWorkflowWithDefaults(workflow);
                const updatedWorkflow = await updateWorkflow(this.map[workflow.id], workflow, applyToAggregation) as WorkflowRecord;
                this.setMapAtKey({ id: updatedWorkflow.id, workflow: updatedWorkflow });
                sendReportObjectMetadata(updatedWorkflow);
            } catch (error) {
                throw error;
            } finally {
                this.setSaving({ isSaving: false });
            }
        },
        async remove ({ id }: { id: string }) {
            this.setRemoving({ isRemoving: true });
            try {
                await deleteWorkflow(id);
                this.deleteMapKey({ id });
            } catch (error) {
                throw error;
            } finally {
                this.setRemoving({ isRemoving: false });
            }
        },
        toggleIncludedStep (step: IncludedStep) {
            const updatedSteps = cloneDeep(this.includedSteps) as Record<string, unknown>;
            if (step.stepId) {
                step.id = step.stepId;
                step.label = step.name;
                step.type = step.featureId ? 'featureId' : 'pageId';
            }

            if (updatedSteps[step.id]) {
                delete updatedSteps[step.id];
            } else {
                updatedSteps[step.id] = {
                    id: step.id,
                    label: step.name,
                    type: step.type,
                };
            }

            this.setIncludedSteps({ updatedSteps });
        },
        updateWorkflowSetting ({ workflowId, name, value }: { workflowId: string; name: string; value: unknown }) {
            const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);
            const workflowsSubSettingsName = get(workflowsSubSettings, 'name', null);
            const workflowsSettingsMap = workflowsSubSettingsName
                ? cloneDeep((workflowsSubSettings as UserSetting<Record<string, unknown>>).value)
                : {};

            set(workflowsSettingsMap, `${workflowId}.${name}`, value);

            useUserSettingsStore().update({
                name: WORKFLOWS_SUB_USER_SETTING_NAME,
                value: JSON.stringify(workflowsSettingsMap),
                namespaced: true,
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useWorkflowsStore, module.hot));
}
